// One faint signal trace in the left margin. The path is measured from the
// document itself: a node beside each [data-signal-label] section, gentle
// 45° jogs between them, an accent marker that follows the reader, and a rare
// meteor. Wide viewports only; prefers-reduced-motion gets a still line.

const SVG_NS = "http://www.w3.org/2000/svg";
const WIDTH = 120;
const JOG_X = [54, 66];
const NODE_RADIUS = 2.2;
const MARKER_RADIUS = 2.6;
const METEOR_FIRST_MS = 15000;
const METEOR_EVERY_MS = 75000;
const METEOR_DURATION_MS = 1600;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const svgElement = (name, attributes = {}) => {
  const node = document.createElementNS(SVG_NS, name);
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
  return node;
};

export default function initPageSignal() {
  const host = document.querySelector("[data-page-signal]");
  const progressBar = document.querySelector("[data-scroll-progress]");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const wideViewport = window.matchMedia("(min-width: 1400px)");

  let path = null;
  let progressPath = null;
  let marker = null;
  let meteor = null;
  let pathLength = 0;
  let startY = 0;
  let endY = 0;
  let scrollScheduled = false;
  let rebuildTimer = 0;
  let meteorTimer = 0;
  let meteorFrame = 0;

  const teardown = () => {
    if (!host) return;
    host.innerHTML = "";
    path = progressPath = marker = meteor = null;
    pathLength = 0;
  };

  const build = () => {
    if (!host || !wideViewport.matches) return;

    const sections = [...document.querySelectorAll("[data-signal-label]")];
    const footer = document.querySelector(".footer");
    if (!sections.length || !footer) return;

    teardown();

    const nodes = sections.map((section) => {
      const bounds = section.getBoundingClientRect();
      return {
        label: section.dataset.signalLabel,
        y: Math.round(bounds.top + window.scrollY + 12)
      };
    });

    startY = Math.round(window.innerHeight * 0.82);
    endY = Math.round(footer.getBoundingClientRect().top + window.scrollY - 32);
    if (endY <= startY) return;

    let jog = 0;
    let x = JOG_X[jog];
    let d = `M${x} ${startY}`;
    const drawnNodes = [];

    nodes.forEach((node, index) => {
      if (node.y <= startY || node.y >= endY) return;
      d += ` V${node.y}`;
      drawnNodes.push({ ...node, x });

      // A gentle 45° jog after each node keeps the routing feel without noise.
      const nextX = JOG_X[(jog + 1) % JOG_X.length];
      const jogStart = node.y + 30;
      d += ` V${jogStart} L${nextX} ${jogStart + Math.abs(nextX - x)}`;
      x = nextX;
      jog += 1;
    });

    d += ` V${endY}`;

    const svg = svgElement("svg", {
      width: WIDTH,
      height: endY + 20,
      viewBox: `0 0 ${WIDTH} ${endY + 20}`
    });

    path = svgElement("path", { class: "page-signal__base", d });
    svg.appendChild(path);

    drawnNodes.forEach((node) => {
      svg.appendChild(
        svgElement("circle", {
          class: "page-signal__node",
          cx: node.x,
          cy: node.y,
          r: NODE_RADIUS
        })
      );
      const label = svgElement("text", {
        class: "page-signal__label",
        x: node.x - 12,
        y: node.y + 3.5,
        "text-anchor": "end"
      });
      label.textContent = node.label;
      svg.appendChild(label);
    });

    if (!reducedMotion.matches) {
      progressPath = svgElement("path", { class: "page-signal__progress", d });
      marker = svgElement("circle", { class: "page-signal__marker", r: MARKER_RADIUS });
      meteor = svgElement("circle", { class: "page-signal__meteor", r: 2 });
      svg.appendChild(progressPath);
      svg.appendChild(meteor);
      svg.appendChild(marker);
    }

    host.appendChild(svg);

    if (progressPath) {
      pathLength = progressPath.getTotalLength();
      progressPath.style.strokeDasharray = `${pathLength}`;
      progressPath.style.strokeDashoffset = `${pathLength}`;
    }

    update();
  };

  const update = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const ratio = scrollable > 0 ? clamp(window.scrollY / scrollable, 0, 1) : 0;

    if (progressBar) {
      progressBar.style.transform = `scaleX(${ratio})`;
    }

    if (progressPath && pathLength) {
      const readerY = window.scrollY + window.innerHeight * 0.55;
      const along = clamp((readerY - startY) / (endY - startY), 0, 1) * pathLength;
      progressPath.style.strokeDashoffset = `${pathLength - along}`;

      if (marker) {
        const point = progressPath.getPointAtLength(along);
        marker.setAttribute("cx", point.x.toFixed(2));
        marker.setAttribute("cy", point.y.toFixed(2));
      }
    }

    scrollScheduled = false;
  };

  const scheduleUpdate = () => {
    if (scrollScheduled) return;
    scrollScheduled = true;
    window.requestAnimationFrame(update);
  };

  const scheduleRebuild = () => {
    window.clearTimeout(rebuildTimer);
    rebuildTimer = window.setTimeout(() => {
      if (wideViewport.matches) build();
      else teardown();
      update();
    }, 220);
  };

  // The rare playful moment: a single spark runs the trace, then silence.
  const runMeteor = () => {
    if (!meteor || !progressPath || !pathLength) return;
    if (document.hidden || reducedMotion.matches) return;

    const startedAt = performance.now();

    const step = (now) => {
      const t = (now - startedAt) / METEOR_DURATION_MS;
      if (t >= 1) {
        meteor.style.opacity = "0";
        meteorFrame = 0;
        return;
      }
      const point = progressPath.getPointAtLength(pathLength * t);
      meteor.setAttribute("cx", point.x.toFixed(2));
      meteor.setAttribute("cy", point.y.toFixed(2));
      meteor.style.opacity = `${0.85 * (1 - t * 0.6)}`;
      meteorFrame = window.requestAnimationFrame(step);
    };

    window.cancelAnimationFrame(meteorFrame);
    meteorFrame = window.requestAnimationFrame(step);
  };

  window.addEventListener("scroll", scheduleUpdate, { passive: true });
  window.addEventListener("resize", scheduleRebuild, { passive: true });
  wideViewport.addEventListener?.("change", scheduleRebuild);
  reducedMotion.addEventListener?.("change", scheduleRebuild);

  if ("ResizeObserver" in window) {
    // Content height moves as fonts, images, and Wikipedia extracts arrive.
    new ResizeObserver(scheduleRebuild).observe(document.body);
  }

  window.addEventListener("load", scheduleRebuild, { once: true });

  meteorTimer = window.setTimeout(() => {
    runMeteor();
    meteorTimer = window.setInterval(runMeteor, METEOR_EVERY_MS);
  }, METEOR_FIRST_MS);

  build();
  update();
}
